const API_BASE_URL = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");

export const saveAuthUser = (user) => {
  if (!user) return;
  localStorage.setItem("user", JSON.stringify(user));
};

export const getAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

export const clearAuthUser = () => {
  localStorage.removeItem("user");
};

const request = async (endpoint, method = "GET", data, opts = {}) => {
  const { redirectOnAuthFail = true } = opts;

  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: data ? JSON.stringify(data) : undefined,
  });

  const raw = await res.text();

  let parsed;
  try {
    parsed = raw ? JSON.parse(raw) : {};
  } catch {
    parsed = raw;
  }

  const isAuthFail = res.status === 401 || res.status === 403;

  if (isAuthFail) {
    clearAuthUser();

    if (redirectOnAuthFail && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }

    const err = new Error(
      parsed?.message || (res.status === 401 ? "Unauthorized" : "Forbidden")
    );
    err.status = res.status;
    err.response = {
      status: res.status,
      data: parsed,
    };
    throw err;
  }

  if (!res.ok) {
    const err = new Error(
      parsed?.message || `${method} ${endpoint} failed`
    );
    err.status = res.status;
    err.response = {
      status: res.status,
      data: parsed,
    };
    throw err;
  }

  return parsed;
};

const qs = (params = {}) => {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    s.append(k, String(v));
  });
  const str = s.toString();
  return str ? `?${str}` : "";
};

export const loginUser = async (username, password) => {
  const res = await request(
    "/auth/login",
    "POST",
    { username, password },
    { redirectOnAuthFail: false }
  );

  if (res?.user) saveAuthUser(res.user);
  return res;
};

export const getMe = () =>
  request("/auth/me", "GET", null, { redirectOnAuthFail: false });

export const restoreSession = async () => {
  try {
    const me = await getMe();
    if (me) saveAuthUser(me);
    return me || null;
  } catch {
    clearAuthUser();
    return null;
  }
};

export const logoutUser = async () => {
  await request("/auth/logout", "POST", null, { redirectOnAuthFail: false });
  clearAuthUser();
  window.location.assign("/login");
};

// USERS
export const getUsers = () =>
  request("/users", "GET", null, { redirectOnAuthFail: false });
export const createUser = (data) => request("/users/create", "POST", data);
export const deactivateUser = (id) => request(`/users/deactivate/${id}`, "PUT");
export const updateUser = (id, data) => request(`/users/edit/${id}`, "PUT", data);
export const deleteUser = (id) => request(`/users/delete/${id}`, "DELETE");
export const changePassword = (currentPassword, newPassword) =>
  request("/auth/change-password", "POST", { currentPassword, newPassword });

// ROLES
export const getRoles = () =>
  request("/roles", "GET", null, { redirectOnAuthFail: false });

// REPORTS
export const rptTransactions = (params) =>
  request(`/reports/transactions${qs(params)}`);

export const rptAuditTrail = (params) =>
  request(`/reports/audit-trail${qs(params)}`);

export const rptDeliveriesPerClient = (params) =>
  request(`/reports/deliveries-per-client${qs(params)}`);

export const rptCancelReturn = (params) =>
  request(`/reports/cancel-return${qs(params)}`);

export const rptPoPerSupplier = (params) =>
  request(`/reports/po-per-supplier${qs(params)}`);

export const rptReceivingPO = (params) =>
  request(`/reports/receiving-po${qs(params)}`);

export const rptExpiryItems = (params) =>
  request(`/reports/expiry-items${qs(params)}`);

export const rptSalesOrders = (params = {}) =>
  request(`/reports/sales-orders${qs(params)}`);

export const rptDeliveriesClientDNs = (params = {}) =>
  request(`/reports/deliveries-per-client/dns${qs(params)}`);

export const rptClientPurchaseClients = (params = {}) =>
  request(`/reports/client-purchases/clients${qs(params)}`);

export const rptClientPurchaseClientItems = (params = {}) =>
  request(`/reports/client-purchases/clients/items${qs(params)}`);

export const getDNPrintBundle = (dnid) => request(`/dn/print-bundle/${dnid}`);

// STOCK TRANSFER
export const createStockTransfer = (payload) =>
  request("/stock-transfer/create", "POST", payload);

export const getStockTransfer = (code) =>
  request(`/stock-transfer/${code}`);

export const scanStockTransfer = (transferId, barcode) =>
  request(`/stock-transfer/${transferId}/scan`, "POST", { barcode });

export const confirmStockTransfer = (transferId) =>
  request("/stock-transfer/confirm", "POST", { transferId });

export const cancelStockTransfer = (transferId, cancelReason) =>
  request("/stock-transfer/cancel", "POST", { transferId, cancelReason });

export const listStockTransfers = (statusOrParams = "PENDING", limit = 10) => {
  const params =
    typeof statusOrParams === "object" && statusOrParams !== null
      ? statusOrParams
      : {
        status: statusOrParams,
        page: 1,
        pageSize: limit,
      };

  return request(`/stock-transfer/list${qs(params)}`);
};

export const getIncomingStocksFromPO = () =>
  request("/stock/incoming-from-po");

// ITEMS
export const getItems = () => request("/items");
export const addItem = (item) => request("/items", "POST", item);
export const updateItem = (id, item) => request(`/items/${id}`, "PUT", item);
export const deleteItem = (id) => request(`/items/${id}`, "DELETE");

// SUPPLIERS
export const getSuppliers = () => request("/suppliers");
export const addSupplier = (supplier) => request("/suppliers", "POST", supplier);
export const updateSupplier = (id, supplier) =>
  request(`/suppliers/${id}`, "PUT", supplier);
export const deleteSupplier = (id) => request(`/suppliers/${id}`, "DELETE");

// PURCHASE ORDERS
export const getPurchaseOrders = () => request("/purchase-orders");
export const getPurchaseOrder = (id) => request(`/purchase-orders/${id}`);
export const addPurchaseOrder = (data) =>
  request("/purchase-orders", "POST", data);
export const receivePurchaseOrder = (poid, data) =>
  request(`/purchase-orders/receive/${poid}`, "PUT", data);
export const getNextPONumber = () => request("/purchase-orders/generate-number");

// STOCK
export const getPendingStockItems = () => request("/stock/pending");
export const addToStock = (data) => request("/stock/add", "POST", data);
export const getStocks = () => request("/stock");
export const scanBarcode = (barcode) =>
  request("/stock/scan", "POST", { barcode });
export const stockOut = (data) => request("/stock/out", "POST", data);

export const returnStock = (barcode) =>
  request("/stock/return", "POST", { barcode });

export const getStockList = (supplierID = "all") => {
  const endpoint =
    supplierID === "all" ? "/stock/list" : `/stock/list?supplierID=${supplierID}`;
  return request(endpoint);
};

export const getStockMovement = (itemID, lotNumber) =>
  request(`/stock/movement?itemID=${itemID}&lotNumber=${lotNumber}`);

export const expireStock = (stockID) =>
  request("/stock/expire", "POST", { stockID });

export const expireLot = (payload) =>
  request("/stock/expire-lot", "POST", payload);

export const getStockTransactions = () => request("/stock/transactions");
// export const getLotsByItem = (itemId) => request(`/stock/lots/${itemId}`);
export const getLotsByItem = (
  itemId,
  warehouseID = 0,
  currentSOID = 0
) => {
  const params = new URLSearchParams({
    warehouseID: String(warehouseID || 0),
    currentSOID: String(currentSOID || 0),
  });

  return request(`/stock/lots/${itemId}?${params.toString()}`);
};

export const getStockByBarcode = (barcode) => request(`/stock/barcode/${barcode}`);

export const getBarcodeInfo = (barcode) =>
  request(`/stock/barcode-info/${encodeURIComponent(barcode)}`);

export const transferStock = (data) => request("/stock/transfer", "POST", data);

export const getLotBarcodes = (itemID, lotNumber, warehouseID) =>
  request(
    `/stock/lot-barcodes?itemID=${itemID}&lotNumber=${lotNumber}&warehouseID=${warehouseID}`
  );

export const getLotBarcodesByCatalog = (catalogNo, lotNumber, warehouseID) =>
  request(
    `/stock/lot-barcodes-by-catalog${qs({
      catalogNo,
      lotNumber,
      warehouseID,
    })}`
  );

export const getItemsInStock = () => request("/stock/items-in-stock");
export const getItemsWNWOStock = () =>
  request("/stock/items-with-and-without-stock");

export const returnDNBarcode = (dnid, barcode, reason) =>
  request(`/dn/${dnid}/return`, "POST", { barcode, reason });

export const cancelDN = (dnid, reason, barcodes = []) =>
  request(`/dn/${dnid}/cancel`, "POST", { reason, barcodes });

export const addOpeningStock = (data) =>
  request("/stock/manual-add", "POST", data);

// export const manualStockOut = (barcode, qty) =>
//   request("/stock/manual-out", "POST", { barcode, qty });
export async function manualStockOut(barcode, qty, options = {}) {
  const payload = {
    barcode,
    fullBoxOut: Boolean(options.fullBoxOut),
  };

  if (qty !== null && qty !== undefined && qty !== "") {
    payload.qty = Number(qty);
  }

  const res = await fetch(`${API_BASE_URL}/stock/manual-out`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.error || data?.message || "Manual stock out failed.");
  }

  return data;
}

export const getItemsSummary = () => request("/stock/items-summary");

export const getItemDetails = (itemId) =>
  request(`/stock/item-details/${itemId}`);

export const getItemDetailsByCatalog = (catalogNo) =>
  request(`/stock/item-details-by-catalog/${encodeURIComponent(catalogNo)}`);

export const getItemsMovementReport = (params = {}) =>
  request(`/stock/items-movement-report${qs(params)}`);

export const getItemMovementReportByCatalog = (params = {}) =>
  request(`/stock/item-movement-report-by-catalog${qs(params)}`);

// WAREHOUSE
export const getWarehouses = () => request("/warehouses");
export const addWarehouse = (warehouse) =>
  request("/warehouses", "POST", warehouse);
export const updateWarehouse = (id, warehouse) =>
  request(`/warehouses/${id}`, "PUT", warehouse);
export const deleteWarehouse = (id) => request(`/warehouses/${id}`, "DELETE");
export const getStockListFiltered = (endpoint) => request(endpoint);


// DASHBOARD
export const getDashboardSummary = () => request("/dashboard/summary");
export const getDashboardChart = (type) =>
  request(`/dashboard/chart/${type}`);
export const getItemStockSummary = async (page = 1) => {
  return request(`/dashboard/items-stock-summary?page=${page}`);
};

// MACHINES
export const getMachines = () => request("/machines");
export const addMachine = (machine) => request("/machines", "POST", machine);
export const updateMachine = (id, machine) =>
  request(`/machines/${id}`, "PUT", machine);
export const deleteMachine = (id) => request(`/machines/${id}`, "DELETE");

// MACHINE SUPPLIERS
export const getMachineSuppliers = () => request("/machine-suppliers");
export const addMachineSupplier = (supplier) =>
  request("/machine-suppliers", "POST", supplier);
export const updateMachineSupplier = (id, supplier) =>
  request(`/machine-suppliers/${id}`, "PUT", supplier);
export const deleteMachineSupplier = (id) =>
  request(`/machine-suppliers/${id}`, "DELETE");

// SALESPERSONS
export const getSalespersons = () => request("/salespersons");
export const addSalesperson = (data) =>
  request("/salespersons", "POST", data);
export const updateSalesperson = (id, data) =>
  request(`/salespersons/${id}`, "PUT", data);
export const deleteSalesperson = (id) =>
  request(`/salespersons/${id}`, "DELETE");

// CUSTOMERS
export const getCustomers = () => request("/customers");
export const getCustomer = (id) => request(`/customers/${id}`);
export const addCustomer = (customer) =>
  request("/customers", "POST", customer);
export const updateCustomer = (id, customer) =>
  request(`/customers/${id}`, "PUT", customer);
export const deleteCustomer = (id) =>
  request(`/customers/${id}`, "DELETE");

// SALES ORDERS
export const getSalesOrders = () => request("/sales-orders");
export const getSalesOrder = (id) => request(`/sales-orders/${id}`);

export const getSalesOrderTimeline = (soid) =>
  request(`/sales-order-timeline/${soid}`);

export const addSalesOrder = (data) =>
  request("/sales-orders", "POST", data);
export const updateSalesOrder = (id, data) =>
  request(`/sales-orders/${id}`, "PUT", data);

export const getNextSONumber = () => request("/sales-orders/generate-number");

export async function cancelSalesOrder(soid) {
  return request(`/sales-orders/${soid}/cancel`, "PUT");
}

export const approveSalesOrder = (soid, remarks = "") =>
  request(`/sales-orders/${soid}/approve`, "PUT", { remarks });

export const disapproveSalesOrder = (soid, remarks) =>
  request(`/sales-orders/${soid}/disapprove`, "PUT", { remarks });

export const savePendingApprovalRemarks = (soid, remarks = "") =>
  request(`/sales-orders/${soid}/pending-remarks`, "PUT", { remarks });

// LOT APPROVAL
// export const approveSalesOrderLots = (soid, remarks = "") =>
//   request(`/sales-orders/${soid}/approve-lots`, "PUT", { remarks });

export const approveSalesOrderLots = (soid, payload = {}) => {
  const body =
    typeof payload === "string"
      ? { remarks: payload }
      : payload || {};

  return request(`/sales-orders/${soid}/approve-lots`, "PUT", body);
};

export const updateLots = (detailId, lots) =>
  request(`/sales-orders/details/${detailId}/lots`, "PUT", { lots });

export const recomputeDeliveryStatus = (soid) =>
  request(`/sales-orders/${soid}/recompute-delivery-status`, "PUT");

export const uploadSalesOrderAttachment = async (soid, file) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE_URL}/sales-orders/${soid}/attachments`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  const raw = await res.text();

  let parsed;
  try {
    parsed = raw ? JSON.parse(raw) : {};
  } catch {
    parsed = raw;
  }

  if (!res.ok) {
    const err = new Error(parsed?.error || "Failed to upload sales order attachment");
    err.status = res.status;
    err.response = {
      status: res.status,
      data: parsed,
    };
    throw err;
  }

  return parsed;
};

export const getSalesOrderAttachments = (soid) =>
  request(`/sales-orders/${soid}/attachments`);

export const deleteSalesOrderAttachment = (id) =>
  request(`/sales-orders/attachments/${id}`, "DELETE");

// BATCHES
export const getSOBatches = (soid) => request(`/sales-orders/${soid}/batches`);
export const createSOBatch = (soid, payload) =>
  request(`/sales-orders/${soid}/batches`, "POST", payload);

export const getBatchLots = (batchId) => request(`/so-batches/${batchId}/lots`);
export const saveBatchItemLots = (batchId, sodetailId, lots) =>
  request(`/so-batches/${batchId}/details/${sodetailId}/lots`, "PUT", { lots });

export const getBatchItems = (batchId) => request(`/so-batches/${batchId}/items`);

export const getDeliveredLotsBySO = (soid) =>
  request(`/sales-orders/delivered-lots/${soid}`);

export const getDeliveredLotsByBatch = (batchId) =>
  request(`/sales-orders/batch-delivered-lots/${batchId}`);

export const getDNItemsDetailBySO = (soid) =>
  request(`/dn/items-detail/by-so/${soid}`);

// DN
export const createDN = (data) => request("/dn/create", "POST", data);
export const addDNItem = (data) => request("/dn-items/add", "POST", data);
export const getDNItems = (dnid) => request(`/dn-items/${dnid}`);
export const getDeliveryStatus = (soid) =>
  request(`/dn/delivery-notes/status/${soid}`);
export const getDNBySO = (soid) => request(`/dn/header/${soid}`);
export const getDNHeadersBySO = (soid) => request(`/dn/by-so/${soid}`);
export const getDNItemsBySOBatch = (soid, batchId) =>
  request(`/dn/items/by-so/${soid}?batchId=${batchId}`);
export const getDNItemsByDNID = (dnid) => request(`/dn/by-dn/${dnid}`);
export const getDNItemsBySO = (soid) => request(`/dn-items/by-so/${soid}`);

export const getDNHeaderByDNID = async (dnid, soid) => {
  const headers = await request(`/dn/by-so/${soid}`);
  return (headers || []).find((d) => Number(d.DNID) === Number(dnid)) || null;
};

export const listDNPrintEdits = (dnid) => request(`/dn-print-edits/${dnid}`);
export const getDNPrintEdit = (editid) =>
  request(`/dn-print-edits/edit/${editid}`);
export const createDNPrintEdit = (dnid, payload) =>
  request(`/dn-print-edits/${dnid}`, "POST", payload);

// export const markDNDelivered = (dnid) =>
//   request(`/dn/${dnid}/mark-delivered`, "PUT");
export const markDNDelivered = (dnid, deliveredDate) =>
  request(`/dn/${dnid}/mark-delivered`, "PUT", { deliveredDate });

// PRINTER
export const printZebraLabel = async (zpl) => {
  const res = await fetch(`${API_BASE_URL}/print-label`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ zpl }),
  });

  if (!res.ok) throw new Error("Printer error");
  return await res.json();
};

const apiService = {
  loginUser,
  logoutUser,
  getMe,
  restoreSession,
  saveAuthUser,
  getAuthUser,
  clearAuthUser,

  getUsers,
  createUser,
  deactivateUser,
  getRoles,
  updateUser,
  deleteUser,
  changePassword,

  rptTransactions,
  rptAuditTrail,
  rptDeliveriesPerClient,
  rptCancelReturn,
  rptPoPerSupplier,
  rptReceivingPO,
  rptExpiryItems,
  rptSalesOrders,
  rptDeliveriesClientDNs,
  rptClientPurchaseClients,
  rptClientPurchaseClientItems,
  getDNPrintBundle,

  createStockTransfer,
  getStockTransfer,
  scanStockTransfer,
  confirmStockTransfer,
  cancelStockTransfer,
  listStockTransfers,
  getIncomingStocksFromPO,

  getItems,
  addItem,
  updateItem,
  deleteItem,

  getSuppliers,
  addSupplier,
  updateSupplier,
  deleteSupplier,

  getPurchaseOrders,
  getPurchaseOrder,
  addPurchaseOrder,
  receivePurchaseOrder,
  getNextPONumber,

  getPendingStockItems,
  addToStock,
  getStocks,
  scanBarcode,
  stockOut,
  returnStock,
  getStockList,
  getStockMovement,
  expireStock,
  expireLot,
  getStockTransactions,
  getLotsByItem,
  getStockByBarcode,
  transferStock,
  getLotBarcodes,
  getLotBarcodesByCatalog,
  getItemsInStock,
  getItemsWNWOStock,
  returnDNBarcode,
  cancelDN,
  addOpeningStock,
  getItemsSummary,
  getItemDetails,
  getItemDetailsByCatalog,
  manualStockOut,
  getItemsMovementReport,
  getItemMovementReportByCatalog,
  getBarcodeInfo,

  getWarehouses,
  addWarehouse,
  updateWarehouse,
  deleteWarehouse,
  getStockListFiltered,

  getDashboardSummary,
  getDashboardChart,
  getItemStockSummary,

  getMachines,
  addMachine,
  updateMachine,
  deleteMachine,

  getMachineSuppliers,
  addMachineSupplier,
  updateMachineSupplier,
  deleteMachineSupplier,

  getCustomers,
  getCustomer,
  addCustomer,
  updateCustomer,
  deleteCustomer,

  getSalespersons,
  addSalesperson,
  updateSalesperson,
  deleteSalesperson,

  getSalesOrders,
  getSalesOrder,
  getSalesOrderTimeline,
  addSalesOrder,
  updateSalesOrder,
  getNextSONumber,
  cancelSalesOrder,
  approveSalesOrder,
  disapproveSalesOrder,
  savePendingApprovalRemarks,
  approveSalesOrderLots,
  updateLots,
  recomputeDeliveryStatus,
  getSOBatches,
  createSOBatch,
  getBatchLots,
  saveBatchItemLots,
  getBatchItems,
  getDeliveredLotsBySO,
  getDeliveredLotsByBatch,
  getDNItemsBySOBatch,
  getDNItemsDetailBySO,
  uploadSalesOrderAttachment,
  getSalesOrderAttachments,
  deleteSalesOrderAttachment,

  createDN,
  addDNItem,
  getDNItems,
  getDeliveryStatus,
  getDNItemsBySO,
  getDNBySO,
  getDNHeadersBySO,
  getDNItemsByDNID,
  listDNPrintEdits,
  createDNPrintEdit,
  getDNPrintEdit,
  getDNHeaderByDNID,

  printZebraLabel,
};

export default apiService;